import { Component, OnInit, OnDestroy, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject, timer, switchMap, takeUntil, tap } from 'rxjs';
import { TimesheetsApiService } from 'shared/services/api/timesheets.api.service';
import { DashboardApiService } from 'shared/services/api/dashboard.api.service';
import { DocumentMgmtApiService } from 'shared/services/api/document-mgmt.api.service';
import { ManagementApiService } from 'shared/services/api/management.api.service';
import { SettingsService } from 'shared/services/settings.service';
import { DashboardCounters } from 'shared/models/dashboard-counters';
import { UserSettings } from 'shared/models/user-settings.model';
import { EmployeePermission } from 'shared/application-data';
import { APIResponse } from 'shared/models/api-response';
import { HrDailyTimesheetDetailsDialogComponent, HrDailyTimesheetDetailsDialogData } from 'shared/dialogs/hr-daily-timesheet-details-dialog/hr-daily-timesheet-details-dialog.component';
import { PlannerEventsApiService } from 'shared/services/api/planner-events.api.service';
import { DateTime } from 'luxon';

export interface ModifiedNotification {
  _id: string;
  projectName: string;
  date: string;
  lastModifiedDate: string;
  lastModifiedBy: string;
  lastModifiedByName?: string;
}

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [
    CommonModule, MatButtonModule, MatIconModule, MatMenuModule,
    MatTooltipModule, MatBadgeModule, MatDividerModule
  ],
  templateUrl: './notification-bell.component.html'
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private _timesheetsApi = inject(TimesheetsApiService);
  private _dashboardApi = inject(DashboardApiService);
  private _docMgmtApi = inject(DocumentMgmtApiService);
  private _managementApi = inject(ManagementApiService);
  private _settingsService = inject(SettingsService);
  private _router = inject(Router);
  private _matDialog = inject(MatDialog);
  private _plannerApi = inject(PlannerEventsApiService);
  private _destroy$ = new Subject<void>();

  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  notifications: ModifiedNotification[] = [];
  expiringDocuments: any[] = [];
  expiringEquipmentDocuments: any[] = [];
  plannerReminders: any[] = [];
  counters = new DashboardCounters();
  hasViewPermission = false;
  isExpiringDismissed = false;
  isExpiredDismissed = false;
  
  get count(): number {
    if (!this.hasViewPermission) return 0;
    return this.notifications.length + 
           this.expiringDocuments.length +
           this.expiringEquipmentDocuments.length +
           this.plannerReminders.length +
           this.counters.pendingProjectRequestsCount + 
           this.counters.pendingMarkoutRequestsCount + 
           (this.isExpiringDismissed ? 0 : this.counters.expiringCount) + 
           (this.isExpiredDismissed ? 0 : this.counters.expiredCount);
  }

  ngOnInit(): void {
    this._settingsService.getUserSettings().pipe(
      takeUntil(this._destroy$)
    ).subscribe(settings => {
      if (settings?.user) {
        const wasAllowed = this.hasViewPermission;
        if (settings.user.role === 'employee' && !settings.user.permissions?.includes(EmployeePermission.VIEW_NOTIFICATIONS)) {
          this.hasViewPermission = false;
        } else {
          this.hasViewPermission = true;
        }
        if (this.hasViewPermission && !wasAllowed) {
          this.loadNotifications();
        }
      }
    });

    this._dashboardApi.counters$.pipe(
      takeUntil(this._destroy$)
    ).subscribe(counters => {
      this.counters = counters;
      const dismissedExpired = parseInt(localStorage.getItem('dismissed_expired_count') || '0', 10);
      this.isExpiredDismissed = counters.expiredCount <= dismissedExpired;

      const dismissedExpiring = parseInt(localStorage.getItem('dismissed_expiring_count') || '0', 10);
      this.isExpiringDismissed = counters.expiringCount <= dismissedExpiring;
    });

    this.loadNotifications();
    this.loadExpiringDocuments();
    this.loadExpiringEquipmentDocuments();
    this.loadPlannerReminders();

    // Instantly refresh when a document is updated (no polling delay)
    this._docMgmtApi.documentUpdated$.pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => this.loadExpiringDocuments());

    // Instantly refresh when an equipment document is updated
    this._managementApi.equipmentDocumentUpdated$.pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => this.loadExpiringEquipmentDocuments());

    // Poll every 15 seconds — good balance of responsiveness vs server load
    timer(15000, 15000).pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this.loadNotifications();
      this.loadExpiringDocuments();
      this.loadExpiringEquipmentDocuments();
      this.loadPlannerReminders();
    });
  }

  dismissExpired(event: MouseEvent): void {
    event.stopPropagation();
    this.isExpiredDismissed = true;
    localStorage.setItem('dismissed_expired_count', this.counters.expiredCount.toString());
  }

  dismissExpiring(event: MouseEvent): void {
    event.stopPropagation();
    this.isExpiringDismissed = true;
    localStorage.setItem('dismissed_expiring_count', this.counters.expiringCount.toString());
  }

  loadNotifications(): void {
    if (!this.hasViewPermission) return;
    this._timesheetsApi.getModifiedUnreviewed().pipe(
      takeUntil(this._destroy$)
    ).subscribe((res: any) => {
      if (res.success && res.data) {
        this.notifications = res.data.timesheets || [];
      }
    });
  }

  loadExpiringDocuments(): void {
    if (!this.hasViewPermission) return;
    this._docMgmtApi.get().pipe(
      takeUntil(this._destroy$)
    ).subscribe((res: any) => {
      if (res.success && res.data) {
        const now = new Date();
        this.expiringDocuments = (res.data as any[]).filter(doc => {
          if (!doc.expirationDate || doc.isDeleted) return false;
          if (doc.expirationConfirmedAt) return false;

          const expDate = new Date(doc.expirationDate);
          if (isNaN(expDate.getTime())) return false;

          const now = new Date();
          const diffMs = expDate.getTime() - now.getTime();
          const diffDays = diffMs / (1000 * 60 * 60 * 24);

          // Company docs: 2 months (~60 days), Subcontractor docs: 1 month (~30 days)
          // Also show already-expired docs (diffDays < 0) since they definitely need renewal
          const thresholdDays = doc.type === 'OUR_DOCUMENTS' ? 60 : 30;
          return diffDays <= thresholdDays;
        });
      }
    });
  }

  loadExpiringEquipmentDocuments(): void {
    if (!this.hasViewPermission) return;
    this._managementApi.getResourceEquipment({ itemsPerPage: 1000 }).pipe(
      takeUntil(this._destroy$)
    ).subscribe((res: any) => {
      if (res.success && res.data?.results) {
        const now = new Date();
        const expiring: any[] = [];
        (res.data.results as any[]).forEach(eq => {
          if (eq.isDeleted || !eq.documents) return;
          eq.documents.forEach((doc: any) => {
            if (!doc.expirationDate) return;
            if (doc.expirationConfirmedAt) return;

            const expDate = new Date(doc.expirationDate);
            if (isNaN(expDate.getTime())) return;

            const diffMs = expDate.getTime() - now.getTime();
            const diffDays = diffMs / (1000 * 60 * 60 * 24);

            // Equipment documents threshold: 30 days (1 month)
            if (diffDays <= 30) {
              expiring.push({
                ...doc,
                equipmentId: eq._id,
                equipmentName: eq.name
              });
            }
          });
        });
        this.expiringEquipmentDocuments = expiring;
      }
    });
  }

  navigateToEquipmentDocument(doc: any): void {
    this.menuTrigger?.closeMenu();
    this._router.navigate(['/account/management/equipment', doc.equipmentId], {
      queryParams: { editDocId: doc._id }
    });
  }

  navigateToDocument(doc: any): void {
    this.menuTrigger?.closeMenu();
    const tab = doc.type === 'OUR_DOCUMENTS' ? 'company-docs' : 'subcontractor-docs';
    this._router.navigate(['/account/management/document-mgmt'], {
      queryParams: { tab, editDocId: doc._id }
    });
  }

  openTimesheet(notification: ModifiedNotification): void {
    this.menuTrigger?.closeMenu();
    this._timesheetsApi.getById(notification._id).pipe(
      takeUntil(this._destroy$)
    ).subscribe((res: any) => {
      if (res.success && res.data) {
        const dialogData: HrDailyTimesheetDetailsDialogData = {
          timesheet: res.data,
          mode: 'notification-review'
        };
        const dialogRef = this._matDialog.open(HrDailyTimesheetDetailsDialogComponent, {
          data: dialogData,
          panelClass: 'fakturnia-responsive-dialog',
          width: '600px',
          maxHeight: '90vh'
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result === 'approved') {
            this._timesheetsApi.reviewModification(notification._id).pipe(
              takeUntil(this._destroy$)
            ).subscribe((reviewRes: any) => {
              if (reviewRes.success) {
                this.notifications = this.notifications.filter(n => n._id !== notification._id);
              }
            });
          }
        });
      }
    });
  }

  dismiss(notification: ModifiedNotification, event: MouseEvent): void {
    event.stopPropagation();
    this._timesheetsApi.reviewModification(notification._id).pipe(
      takeUntil(this._destroy$)
    ).subscribe((res: any) => {
      if (res.success) {
        this.notifications = this.notifications.filter(n => n._id !== notification._id);
      }
    });
  }

  dismissAll(): void {
    this.menuTrigger?.closeMenu();
    const ids = this.notifications.map(n => n._id);
    ids.forEach(id => {
      this._timesheetsApi.reviewModification(id).pipe(
        takeUntil(this._destroy$)
      ).subscribe();
    });
    this.notifications = [];
  }

  navigateAndDismiss(url: string[], queryParams?: any): void {
    this.menuTrigger?.closeMenu();
    this._router.navigate(url, { queryParams });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  formatTime(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  formatTsDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  loadPlannerReminders(): void {
    if (!this.hasViewPermission) return;
    this._plannerApi.get().pipe(
      takeUntil(this._destroy$)
    ).subscribe((res: any) => {
      if (res.success && res.data) {
        const now = DateTime.now();
        this.plannerReminders = (res.data as any[]).filter(ev => {
          if (!ev.hasReminder || ev.reminderDismissed || ev.isDeleted) return false;
          
          const time = ev.reminderTime || '00:00';
          const dt = DateTime.fromISO(`${ev.fromDate}T${time}`);
          return dt.isValid && now >= dt;
        });
      }
    });
  }

  openReminder(reminder: any): void {
    this.menuTrigger?.closeMenu();
    const updated = { ...reminder, reminderDismissed: true };
    this._plannerApi.update(reminder._id, updated).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.plannerReminders = this.plannerReminders.filter(r => r._id !== reminder._id);
        }
      }
    });
    this._router.navigate(['/account/management/planner/list'], {
      queryParams: { highlightEventId: reminder._id }
    });
  }

  dismissReminder(reminder: any, event: MouseEvent): void {
    event.stopPropagation();
    const updated = { ...reminder, reminderDismissed: true };
    this._plannerApi.update(reminder._id, updated).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.plannerReminders = this.plannerReminders.filter(r => r._id !== reminder._id);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
